import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  FileText,
  Layers,
  Rocket,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { Link } from "react-router";
import { INDUSTRIES } from "../../data/categoryData";

const steps = [
  {
    number: "01",
    title: "Register Your Business",
    shortTitle: "Register",
    icon: Building2,
    summary: "Enter your company and contact details",
    detail:
      "Create your partner account with your company name, business address, contact information, and a designated point of contact. Takes under 3 minutes.",
    items: [],
    accentFrom: "#FFD221",
    accentTo: "#ffec80",
  },
  {
    number: "02",
    title: "Verify Your Business",
    shortTitle: "Verify",
    icon: BadgeCheck,
    summary: "Upload required documents for vetting",
    detail:
      "Our compliance team reviews your submissions within 24–48 hours. All documents are handled securely.",
    items: ["Certificate of Incorporation", "TIN (Tax Identification Number)", "Business Registration"],
    accentFrom: "#FFD221",
    accentTo: "#ffe066",
  },
  {
    number: "03",
    title: "Select Your Category",
    shortTitle: "Category",
    icon: Layers,
    summary: "Choose the verticals you operate in",
    detail:
      "Pick one or more InfoBoxx categories that match your services. Each category has dedicated subcategory lanes for precise lead targeting.",
    items: INDUSTRIES.slice(0, 6).map((ind) => ind.label),
    accentFrom: "#c9a000",
    accentTo: "#FFD221",
  },
  {
    number: "04",
    title: "Set Up Your Profile",
    shortTitle: "Profile",
    icon: UserCheck,
    summary: "Add services, content, and lead preferences",
    detail:
      "Build your provider profile — describe your services, upload content such as guides or FAQs, and specify how you want to receive and handle leads (call, WhatsApp, form, booking).",
    items: [],
    accentFrom: "#FFD221",
    accentTo: "#ffdf44",
  },
  {
    number: "05",
    title: "Get Approved & Go Live",
    shortTitle: "Go Live",
    icon: Rocket,
    summary: "Start receiving qualified, intent-rich leads",
    detail:
      "Once the InfoBoxx team approves your profile, your brand goes live inside the relevant subcategory pages. Users who engage with your content become warm, qualified leads delivered directly to you.",
    items: [],
    accentFrom: "#b8860b",
    accentTo: "#FFD221",
  },
];

const BecomeAPartner = () => {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <section className="relative overflow-hidden bg-white px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
      {/* ambient gold */}
      <div
        className="pointer-events-none absolute right-0 top-0 h-[26rem] w-[26rem] rounded-full bg-[#FFD221]/10 blur-[110px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#FFD221]/08 blur-[90px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          className="mx-auto mb-14 max-w-3xl text-center lg:mb-16"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00] shadow-sm">
            <Sparkles size={14} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
            Partner onboarding
          </span>
          <h2 className="text-[clamp(2rem,4.5vw+0.5rem,3.25rem)] font-black leading-[1.08] tracking-tight text-gray-950">
            How to Become a{" "}
            <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#e8cc4c] bg-clip-text text-transparent">
              Partner
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-gray-500 sm:text-base">
            A streamlined 5-step process — from registration to receiving qualified leads on the platform.
          </p>
        </motion.div>

        {/* Interactive area */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">

          {/* Step nav — left */}
          <div className="relative flex flex-row gap-2 overflow-x-auto pb-1 lg:w-[22rem] lg:flex-shrink-0 lg:flex-col lg:gap-3 lg:overflow-visible lg:pb-0">
            {/* connecting line — desktop only */}
            <div
              className="pointer-events-none absolute left-[1.5625rem] top-6 hidden h-[calc(100%-3rem)] w-px bg-gradient-to-b from-[#FFD221]/70 via-[#FFD221]/30 to-transparent lg:block"
              aria-hidden
            />

            {steps.map((s, i) => {
              const StepIcon = s.icon;
              const done = i < active;
              const selected = i === active;
              return (
                <motion.button
                  key={s.number}
                  type="button"
                  onClick={() => setActive(i)}
                  whileHover={{ x: selected ? 0 : 3 }}
                  whileTap={{ scale: 0.98 }}
                  className={`relative flex min-w-[9rem] items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-300 sm:min-w-[10rem] lg:min-w-0 lg:w-full ${
                    selected
                      ? "border-[#FFD221] bg-[#FFD221] shadow-[0_16px_50px_-24px_rgba(255,210,33,0.65)]"
                      : done
                      ? "border-black/10 bg-[#f7f4ec]"
                      : "border-black/8 bg-[#fafafa] hover:border-black/15 hover:bg-white"
                  }`}
                >
                  <div
                    className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${
                      selected
                        ? "bg-black/10 text-black"
                        : done
                        ? "bg-[#FFD221]/60 text-gray-800"
                        : "bg-white text-gray-500 shadow-sm ring-1 ring-black/[0.06]"
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 size={17} strokeWidth={2.5} className="text-[#9a7b00]" />
                    ) : (
                      <StepIcon size={16} strokeWidth={2.25} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-[10px] font-black uppercase tracking-[0.18em] ${
                        selected ? "text-black/60" : "text-gray-400"
                      }`}
                    >
                      Step {s.number}
                    </p>
                    <p
                      className={`truncate text-sm font-black ${
                        selected ? "text-black" : done ? "text-gray-700" : "text-gray-800"
                      }`}
                    >
                      {s.shortTitle}
                    </p>
                  </div>
                  {selected && (
                    <ChevronRight
                      size={16}
                      strokeWidth={2.5}
                      className="flex-shrink-0 text-black/60 lg:block"
                      aria-hidden
                    />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Detail panel — right */}
          <div className="relative min-h-[22rem] flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, x: 20, scale: 0.98 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -12, scale: 0.99 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden rounded-[2rem] border border-black/[0.08] bg-[#f7f4ec] shadow-[0_24px_80px_-48px_rgba(0,0,0,0.45)]"
              >
                {/* panel top stripe */}
                <div
                  className="h-1.5 w-full"
                  style={{
                    background: `linear-gradient(to right, ${step.accentFrom}, ${step.accentTo})`,
                  }}
                />

                <div className="flex flex-col gap-8 p-6 sm:p-8 lg:p-10">
                  {/* Step badge + icon */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div
                        className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-black shadow-lg ring-2 ring-black/5"
                        style={{
                          background: `linear-gradient(135deg, ${step.accentFrom}, ${step.accentTo})`,
                        }}
                      >
                        <step.icon size={30} strokeWidth={2} aria-hidden />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#c9a000]">
                          Step {step.number}
                        </p>
                        <h3 className="text-xl font-black text-gray-950 sm:text-2xl">{step.title}</h3>
                      </div>
                    </div>
                    <span className="text-[2.5rem] font-black leading-none text-black/[0.04] sm:text-[3.25rem]" aria-hidden>
                      {step.number}
                    </span>
                  </div>

                  {/* Summary pill */}
                  <p className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-4 py-2 text-sm font-semibold text-gray-800">
                    <Briefcase size={14} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
                    {step.summary}
                  </p>

                  {/* Detail text */}
                  <p className="text-sm leading-relaxed text-gray-700 sm:text-[0.9375rem]">{step.detail}</p>

                  {/* Items list */}
                  {step.items.length > 0 && (
                    <div className="rounded-2xl border border-black/[0.07] bg-white p-4 shadow-[0_8px_24px_-16px_rgba(0,0,0,0.25)] sm:p-5">
                      <p className="mb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00]">
                        {active === 1 ? "Required documents" : "Available categories"}
                      </p>
                      <ul className="space-y-2">
                        {step.items.map((item) => (
                          <li key={item} className="flex items-center gap-3 text-sm font-semibold text-gray-800">
                            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#FFD221]/30 text-[11px]">
                              <FileText size={12} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Navigation row */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-1.5">
                      {steps.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActive(i)}
                          aria-label={`Go to step ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            i === active
                              ? "w-8 bg-[#FFD221]"
                              : i < active
                              ? "w-4 bg-[#c9a000]/60"
                              : "w-3 bg-black/15"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {active > 0 && (
                        <button
                          type="button"
                          onClick={() => setActive(active - 1)}
                          className="rounded-xl border border-black/10 bg-white px-4 py-2 text-xs font-black text-gray-700 transition hover:border-black/20 hover:bg-[#f7f4ec]"
                        >
                          Previous
                        </button>
                      )}
                      {active < steps.length - 1 ? (
                        <button
                          type="button"
                          onClick={() => setActive(active + 1)}
                          className="inline-flex items-center gap-2 rounded-xl bg-black px-5 py-2 text-xs font-black text-white transition hover:bg-[#FFD221] hover:text-black"
                        >
                          Next step
                          <ChevronRight size={14} strokeWidth={2.5} />
                        </button>
                      ) : (
                        <Link
                          to="/partners#how-to-become-a-partner"
                          className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-5 py-2 text-xs font-black text-black shadow-[0_8px_24px_-12px_rgba(255,210,33,0.6)] transition hover:bg-[#ffdf44]"
                        >
                          Apply now
                          <Rocket size={14} strokeWidth={2.5} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* CTA row */}
        <motion.div
          className="mt-14 flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center sm:gap-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.15 }}
        >
          <Link
            to="/partners#how-to-become-a-partner"
            className="inline-flex items-center gap-2.5 rounded-2xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-[0_16px_50px_-20px_rgba(255,210,33,0.65)] transition hover:bg-[#ffdf44] hover:shadow-[0_20px_60px_-20px_rgba(255,210,33,0.75)]"
          >
            <span aria-hidden>👉</span>
            Apply to Become a Partner
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 underline-offset-2 transition hover:text-black hover:underline"
          >
            Have questions? Talk to us
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BecomeAPartner;
